import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Switch } from 'antd';
import uniq from 'lodash/uniq';
import { Button, ButtonType, Row, Typography } from 'syngenta-digital-cropwise-react-ui-kit';
import { APIState } from '../../../../models/APIState';
import {
  AmplitudeEventEnum,
  AmplitudePropertyKeyEnum,
  useAmplitude,
} from '../../../../services/amplitudeHelper';
import { defaultFilterState } from '../../constants/constants';
import { useResourceMapperStore } from '../../store/useResourceMapperStore';
import { DateRange, FilterProps, OperationType } from '../../types';
import UnmappedResourceDateRangeFilter from './UnmappedResourceDateRangeFilter';
import UnmappedResourceOperationTypeFilter from './UnmappedResourceOperationTypeFilter';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  evaluateBooleanFeatureFlag,
  LDFeatureFlagEnum,
} from 'tools/feature-flags/launchdarkly-flags';
const { Text } = Typography;

interface UnmappedResourceFiltersProps {
  closeModal: () => void;
  filters: {
    isTankMixShown: FilterProps<boolean>;
    dateRange: FilterProps<DateRange>;
    operationType: FilterProps<OperationType>;
  };
}

const UnmappedResourceFilters = ({ closeModal, filters }: UnmappedResourceFiltersProps) => {
  const { t } = useTranslation();
  const [changedFilters, setChangedFilters] = useState<string[]>([]);
  const trackEvent = useAmplitude();
  const { filtersState, setUnmappedResourceFilters, unmappedResourcesStatus, resetCacheMaps } =
    useResourceMapperStore();
  const flags = useFlags();
  const showOperationType = evaluateBooleanFeatureFlag(
    flags,
    LDFeatureFlagEnum.SHOW_OPERATION_TYPE_FILTER,
  );

  const isLoading = unmappedResourcesStatus === APIState.LOADING;

  const handleToggleTankMix = (value: boolean) => {
    setChangedFilters([...changedFilters, 'Tank Mix']);
    filters.isTankMixShown.update(value);
  };

  const handleApplyClick = () => {
    applyFilters();
    closeModal();
    trackChangedFilters();
  };

  const applyFilters = () => {
    // TODO DB-3173: Resetting cache maps should not be the responsibility of the component long-term
    if (
      filters.dateRange.value.start !== filtersState.dateRange.start ||
      filters.dateRange.value.end !== filtersState.dateRange.end ||
      filters.operationType.value !== filtersState.operationType
    ) {
      resetCacheMaps();
    }

    setUnmappedResourceFilters({
      isTankMixShown: filters.isTankMixShown.value,
      dateRange: filters.dateRange.value,
      operationType: filters.operationType.value,
    });
  };

  const resetFilters = () => {
    filters.isTankMixShown.update(defaultFilterState.isTankMixShown);
    filters.dateRange.update({
      start: defaultFilterState.dateRange.start,
      end: defaultFilterState.dateRange.end,
    });
    filters.operationType.update(defaultFilterState.operationType);
    setChangedFilters([]);
  };

  const trackChangedFilters = () =>
    uniq(changedFilters).forEach((filter) => {
      trackEvent(AmplitudeEventEnum.FILTER_UNMAPPED_PRODUCT_LIST, {
        [AmplitudePropertyKeyEnum.FILTER_ACTION]: `filter applied - ${filter}`,
      });
    });

  const updateDateRangeAndTrack = (dateRange: DateRange) => {
    setChangedFilters([...changedFilters, 'Date']);
    filters.dateRange.update(dateRange);
  };

  const updateOperationTypeAndTrack = (operationType: OperationType) => {
    setChangedFilters([...changedFilters, 'Operation Type']);
    filters.operationType.update(operationType);
  };

  return (
    <div className={'w-287px'} data-testid="filters_popover">
      <Row>
        <span className="font-semibold text-16 leading-24 pb-12">
          {t('resource-mapper.filter')}
        </span>
      </Row>
      <UnmappedResourceDateRangeFilter
        dateRangeFilter={{ value: filters.dateRange.value, update: updateDateRangeAndTrack }}
      />
      {showOperationType && (
        <UnmappedResourceOperationTypeFilter
          operationTypeFilter={{
            value: filters.operationType.value,
            update: updateOperationTypeAndTrack,
          }}
        />
      )}
      <Row justify="space-between" className="mt-16">
        <Text>{t('resource-mapper.show_tank_mix')}</Text>
        <Switch
          data-testid="toggle_tank_mix"
          checked={filters.isTankMixShown.value}
          defaultChecked={filtersState.isTankMixShown}
          onChange={handleToggleTankMix}
          disabled={isLoading}
        />
      </Row>
      <div className="flex justify-between mt-26">
        <Button type={ButtonType.info} data-testid="reset_button" onClick={resetFilters}>
          {t('resource-mapper.reset')}
        </Button>
        <Button type={ButtonType.primary} data-testid="apply_button" onClick={handleApplyClick}>
          {t('resource-mapper.apply')}
        </Button>
      </div>
    </div>
  );
};

export default UnmappedResourceFilters;
